import * as THREE from 'three';
import gsap from 'gsap';
import { loadManifest } from './manifest';
import { initI18n, onLangChange } from './i18n';
import { buildWall, refreshCardTextures, type WorkPlane } from './scene/wall';
import { Controls } from './scene/controls';
import { Lighting } from './scene/lighting';
import { Room } from './scene/room';
import { updateLazyTextures, updateLoadingPulse } from './scene/textures';
import { Transitions } from './scene/transitions';
import { Atmosphere } from './scene/atmosphere';
import { createPostFX } from './scene/postfx';
import { focus } from './scene/focus';
import { fovForViewport } from './scene/camera';
import { DetailView } from './ui/detail';
import { HoverFrame } from './ui/hoverFrame';
import { initHud } from './ui/hud';
import { playIntro } from './ui/intro';
import { showCoachMark } from './ui/coach';
import { showErrorPanel, showLoadingOverlay } from './ui/loading';
import { updateOgTags } from './seo';

interface DeepLink { work?: string; cluster?: string; fromQr: boolean; }

function parseDeepLink(search: string, pathname: string): DeepLink {
  const q = new URLSearchParams(search);
  const fromQr = q.get('from') === 'qr' || /\/wystawa\/?$/.test(pathname);
  const work = q.get('work') || undefined;
  const cluster = q.get('cluster') || (fromQr ? 'wystawa' : undefined);
  return { work, cluster, fromQr };
}

function setWorkUrl(id: string): void {
  const url = new URL(location.href);
  url.searchParams.set('work', id);
  history.replaceState(null, '', url);
}

async function boot() {
  const loading = showLoadingOverlay();
  initI18n();
  const manifest = await loadManifest();
  console.info(`manifest: ${manifest.works.length} works, ${manifest.clusters.length} clusters`);

  const deepLink = parseDeepLink(location.search, location.pathname);
  let pendingWorkId = deepLink.work;
  const wystawaMode = deepLink.fromQr;

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  // Cap DPR at 1.5: the post-FX composer (bloom + DoF + grain) renders the whole
  // scene at this ratio through several full-screen passes, so cost scales with
  // pixel count. 1.5 keeps it crisp on Retina without the ~1.8× cost of 2×.
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.setSize(innerWidth, innerHeight);
  document.body.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(
    fovForViewport(innerWidth, innerHeight), innerWidth / innerHeight, 0.1, 120);

  const wall = buildWall(scene, manifest);
  console.info(`wall: ${wall.planes.length} works in ${wall.bands.length} bands, height ${wall.height.toFixed(1)}`);

  const lighting = new Lighting(scene, wall.width);
  const room = new Room(scene, { width: wall.width, height: wall.height });
  const atmosphere = new Atmosphere(scene, wall.bands, wall.width);
  const detail = new DetailView(manifest);
  const transitions = new Transitions(camera);
  const postfx = createPostFX(renderer, scene, camera);

  async function select(plane: WorkPlane) {
    setWorkUrl(plane.work.id);
    atmosphere.pulseCluster(plane.work.cluster);
    updateOgTags(plane.work);
    transitions.setInkColor(plane.work.palette[0]);
    controls.diveTo(plane);
    // P2 DoF: throw the surrounding hall out of focus, focal plane on the work.
    // The dive eases the camera to ~MIN_STANDOFF..STANDOFF from the wall; the
    // work sits at x=0, so the focal distance ≈ the eased standoff (camera x).
    postfx.enterDof(Math.max(0.6, controls.cameraPos.x));
    await transitions.diveIn();
    detail.open(plane.work, async () => {
      postfx.exitDof();                 // hall snaps back to crisp on return
      await transitions.diveOut();
      controls.resetZoom();
    });
  }

  const frame = new HoverFrame();
  const controls: Controls = new Controls(camera, renderer.domElement, wall, {
    onSelect: select,
    onHoverWork: (id) => atmosphere.pulseCluster(
      wall.planes.find((p) => p.work.id === id)?.work.cluster ?? ''),
    frame,
  });
  controls.enabled = false; // gated until the intro reveal completes

  if (deepLink.cluster) {
    const band = wall.bands.find((b) => b.name === deepLink.cluster);
    if (band) controls.flyToSection(band, 0);
  }

  onLangChange(() => refreshCardTextures(wall));

  const hud = initHud(camera, wall.bands, controls, atmosphere, {
    isDetailOpen: () => detail.isOpen,
  });

  window.addEventListener('resize', () => {
    camera.aspect = innerWidth / innerHeight;
    camera.fov = fovForViewport(innerWidth, innerHeight); // widen on portrait (orientation change)
    camera.updateProjectionMatrix();
    renderer.setSize(innerWidth, innerHeight);
    postfx.resize();
  });

  // On-demand rendering. The composer (bloom + grain + DoF) is the frame cost,
  // and the scene is only ever changing while the camera moves, an ink
  // transition plays, textures are streaming in, or just after user input.
  // When none of that holds we skip the heavy passes — the canvas holds its last
  // frame — which idles the GPU instead of grinding bloom at full rate. Ambient
  // motes and grain freeze when idle; that's the intended trade.
  const IDLE_GRACE_MS = 700; // keep rendering this long after the last activity,
  // long enough to finish trailing tweens (hover pop ~350ms, texture fade ~600ms)
  let activeUntil = performance.now() + 6000; // cover initial load + intro reveal
  const wake = () => { activeUntil = performance.now() + IDLE_GRACE_MS; };
  for (const ev of ['pointerdown', 'pointermove', 'wheel', 'touchstart', 'touchmove', 'keydown']) {
    window.addEventListener(ev, wake, { passive: true });
  }

  gsap.ticker.add((_t, deltaMS) => {
    const dt = deltaMS / 1000;
    // Always run (cheap, no GPU): camera easing/hover + nearby texture loads,
    // so input stays responsive and loads kick off even while idle.
    controls.update();
    updateLazyTextures(wall, camera);

    const now = performance.now();
    const loading = wall.planes.some((p) => p.state === 'loading');
    if (controls.isMoving() || transitions.active || loading) activeUntil = now + IDLE_GRACE_MS;
    if (now > activeUntil) return;    // settled — skip the composer, idle the GPU

    atmosphere.update(dt);
    // P1: drive the shared "follow-you" light pool from the eased camera Y.
    focus.setY(controls.focusY);
    room.update(dt);
    hud.update();
    updateLoadingPulse(wall, dt);
    postfx.update(dt);
    postfx.render();                  // composer → bloom → DoF → vignette+grain → output
    transitions.render(renderer);     // ink overlay still composites on top (autoClear=false)
  });

  window.__app = {
    manifest,
    cameraPos: () => controls.cameraPos,
    openWork: (id) => {
      if (!controls.enabled) return;
      const plane = wall.planes.find((p) => p.work.id === id);
      if (plane) select(plane);
    },
    lookAt: (id) => {
      const plane = wall.planes.find((p) => p.work.id === id);
      const band = plane && wall.bands.find((b) => b.name === plane.work.cluster);
      if (band) controls.flyToSection(band, 0.6);
    },
    focus: (id) => {
      const plane = wall.planes.find((p) => p.work.id === id);
      if (plane) controls.focusForTest(plane);
    },
  };

  updateOgTags();

  const afterIntro = () => {
    controls.enabled = true;
    if (window.__app) window.__app.introDone = true;
    if (wystawaMode) {
      lighting.setWystawaMood();
      hud.openWystawaPanel();
    } else {
      showCoachMark(controls);
    }
    if (pendingWorkId) {
      const plane = wall.planes.find((p) => p.work.id === pendingWorkId);
      pendingWorkId = undefined;
      if (plane) void select(plane);
    }
  };

  loading.dismiss();
  if (wystawaMode) {
    transitions.cover();
    void transitions.dissolve(0).then(afterIntro);
  } else {
    playIntro(transitions).then(afterIntro);
  }
}

boot().catch((err) => { console.error(err); showErrorPanel(err); });
